import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api, { studyFormsAPI, activityAPI } from '@/lib/api';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { useAuth } from '@/lib/auth';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '@/components/ui/dialog';
import {
    Wrench, AlertTriangle, ExternalLink, Globe, FileText, IdCard,
    ClipboardList, Search, Loader2, ChevronRight, History, UserPlus, Copy,
    ClipboardCheck,
} from 'lucide-react';
import { toast } from 'sonner';

const VALNEXO_SITE = 'https://valnexo.fr';
const VALNEXO_PLAQUETTE = 'https://valnexo.fr/presentation-valnexo';

export default function OutilsLiensUtilesPage() {
    const navigate = useNavigate();
    const { user } = useAuth();
    const [showProspectPicker, setShowProspectPicker] = useState(false);
    const [prospects, setProspects] = useState([]);
    const [prospectQuery, setProspectQuery] = useState('');
    const [openingForm, setOpeningForm] = useState(false);
    const [businessCard, setBusinessCard] = useState(null);
    const [loadingCard, setLoadingCard] = useState(true);

    const isAdmin = user?.role === 'admin_full' || user?.role === 'admin' || user?.is_admin === true;
    const isApporteur = user?.role === 'apporteur';
    const isVP = user?.role === 'vp';
    const ONBOARDING_LINK = 'https://valnexo.fr/onboarding';
    const REJOINDRE_LINK = 'https://valnexo.fr/rejoindre';

    useEffect(() => {
        loadBusinessCard();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const loadBusinessCard = async () => {
        setLoadingCard(true);
        try {
            const res = await api.get('/tools/my-business-card');
            setBusinessCard(res.data);
        } catch (err) {
            console.error('Erreur chargement carte de visite', err);
        } finally {
            setLoadingCard(false);
        }
    };

    const openProspectPicker = async () => {
        setProspectQuery('');
        setShowProspectPicker(true);
        if (prospects.length === 0) {
            try {
                const res = await api.get('/prospects?page_size=100000');
                const list = Array.isArray(res.data) ? res.data : (res.data.prospects || []);
                setProspects(list);
            } catch (err) {
                toast.error('Erreur chargement prospects');
            }
        }
    };

    const filteredProspects = (() => {
        if (!prospectQuery.trim()) return prospects.slice(0, 100);
        const q = prospectQuery.toLowerCase().trim();
        const tokens = q.split(/\s+/).filter(Boolean);
        return prospects.filter(p => {
            const hay = [
                p.last_name, p.first_name, p.company_name,
                p.city, p.email, p.phone, p.id?.slice(0, 8),
            ].filter(Boolean).join(' ').toLowerCase();
            return tokens.every(t => hay.includes(t));
        });
    })();

    const handlePickerKeyDown = (e) => {
        if (e.key === 'Enter' && filteredProspects.length > 0 && !openingForm) {
            e.preventDefault();
            handlePickProspect(filteredProspects[0]);
        }
    };

    const handlePickProspect = async (prospect) => {
        setOpeningForm(true);
        try {
            const res = await studyFormsAPI.create(prospect.id, {});
            setShowProspectPicker(false);
            setOpeningForm(false);
            navigate(`/prospects/${prospect.id}/study/${res.data.id}`);
        } catch (err) {
            setOpeningForm(false);
            toast.error(err.response?.data?.detail || 'Erreur création du relevé');
        }
    };

    const openExternal = (url) => {
        activityAPI.toolOpen(url, 'lien_externe').catch(() => {});
        window.open(url, '_blank', 'noopener,noreferrer');
    };

    return (
        <DashboardLayout>
            <div className="space-y-6" data-testid="outils-liens-page">
                {/* Header */}
                <div>
                    <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 flex items-center gap-2">
                        <Wrench className="w-7 h-7 text-blue-600" />
                        Outils &amp; liens utiles
                    </h1>
                    <p className="text-sm text-slate-500 mt-1">
                        Tout ce dont vous avez besoin au quotidien pour rassurer vos prospects et structurer votre discours
                    </p>
                </div>

                {/* === BANNER IMPORTANT === */}
                <Card className="border-2 border-amber-300 bg-gradient-to-br from-amber-50 via-orange-50 to-red-50 shadow-sm" data-testid="important-banner">
                    <CardContent className="p-5 sm:p-6">
                        <div className="flex items-start gap-4">
                            <div className="flex-shrink-0 mt-1">
                                <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
                                    <AlertTriangle className="w-6 h-6 text-amber-600" />
                                </div>
                            </div>
                            <div className="flex-1 space-y-2">
                                <h2 className="text-base font-bold text-amber-900 tracking-wide uppercase">
                                    Important
                                </h2>
                                <p className="text-sm text-slate-800 leading-relaxed">
                                    Ces outils ne sont pas là pour faire joli.
                                </p>
                                <ul className="text-sm text-slate-800 space-y-1 pl-1">
                                    <li>→ Ils donnent une ligne directrice claire</li>
                                    <li>→ Ils rassurent les prospects</li>
                                    <li>→ Ils structurent votre discours</li>
                                </ul>
                                <p className="text-sm text-slate-800 leading-relaxed pt-2">
                                    À chaque premier contact :<br />
                                    → Vous êtes jugés<br />
                                    → Votre sérieux est évalué immédiatement
                                </p>
                                <p className="text-sm text-slate-800 leading-relaxed pt-1">
                                    → <span className="text-emerald-700 font-semibold">Bonne utilisation</span> = hausse du taux de transformation<br />
                                    → <span className="text-rose-700 font-semibold">Mauvaise utilisation</span> = perte de crédibilité + perte de business
                                </p>
                                <div className="pt-2 mt-2 border-t border-amber-200">
                                    <p className="text-sm text-slate-900 font-semibold">Conclusion :</p>
                                    <p className="text-sm text-slate-800 leading-relaxed">
                                        Vous maîtrisez → vous utilisez → vous imposez<br />
                                        <span className="text-slate-600">Sinon, vous freinez votre propre performance</span>
                                    </p>
                                </div>
                            </div>
                        </div>
                    </CardContent>
                </Card>

                {/* === SECTION 1 — Formulaires === */}
                <Card className="border-blue-200 bg-gradient-to-br from-blue-50 via-sky-50 to-indigo-50 overflow-hidden" data-testid="tool-section-forms">
                    <CardContent className="p-5 sm:p-7">
                        <div className="flex flex-col lg:flex-row lg:items-center gap-5">
                            <div className="flex-1">
                                <div className="flex items-center gap-2 mb-2">
                                    <ClipboardList className="w-5 h-5 text-blue-600" />
                                    <h2 className="text-lg font-bold text-slate-900">
                                        Démarrer un nouveau formulaire / relevé d'étude sur place
                                    </h2>
                                </div>
                                <p className="text-sm text-slate-700 leading-relaxed">
                                    Que ce soit pour un client <strong>CEE Tertiaire classique</strong> ou pour le dispositif de
                                    <strong> Rénovation Globale d'Ampleur</strong> (appartement et/ou immeuble collectif),
                                    cliquez sur le bouton, sélectionnez le prospect concerné, choisissez le bon formulaire dans la
                                    plateforme externe puis commencez votre étude.
                                </p>
                                <p className="text-xs text-slate-500 mt-3">
                                    Le formulaire s'ouvre dans un nouvel onglet. À la soumission, il revient automatiquement dans
                                    votre CRM avec toutes les photos et le PDF récapitulatif.
                                </p>
                            </div>
                            <div className="flex-shrink-0 flex flex-col gap-2">
                                <Button
                                    size="lg"
                                    onClick={openProspectPicker}
                                    className="bg-blue-600 hover:bg-blue-700 text-white shadow-lg gap-2"
                                    data-testid="open-form-btn"
                                >
                                    <ExternalLink className="w-5 h-5" />
                                    Nouveau formulaire
                                </Button>
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => navigate('/forms-cee')}
                                    className="text-blue-700 hover:bg-blue-100 gap-1"
                                    data-testid="open-form-history-btn"
                                >
                                    <History className="w-4 h-4" />
                                    Voir l'historique
                                </Button>
                            </div>
                        </div>
                    </CardContent>
                </Card>

                {/* === SECTION 2 — Site internet === */}
                <Card data-testid="tool-section-site">
                    <CardContent className="p-5 sm:p-7">
                        <div className="flex flex-col lg:flex-row lg:items-center gap-5">
                            <div className="flex-shrink-0">
                                <div className="w-14 h-14 rounded-xl bg-emerald-100 flex items-center justify-center">
                                    <Globe className="w-7 h-7 text-emerald-600" />
                                </div>
                            </div>
                            <div className="flex-1">
                                <h3 className="text-lg font-bold text-slate-900">Site internet Valnexo</h3>
                                <p className="text-sm text-slate-600 mt-1">
                                    La référence officielle à partager avec vos prospects pour découvrir l'écosystème complet Valnexo.
                                </p>
                                <p className="text-xs text-slate-400 mt-1 break-all">{VALNEXO_SITE}</p>
                            </div>
                            <div className="flex-shrink-0">
                                <Button onClick={() => openExternal(VALNEXO_SITE)} variant="outline" className="gap-2" data-testid="open-site-btn">
                                    <ExternalLink className="w-4 h-4" />
                                    Ouvrir le site
                                </Button>
                            </div>
                        </div>
                    </CardContent>
                </Card>

                {/* === Lien Candidature « Rejoindre Valnexo » — ÉTAPE 1 (Admin + VP uniquement) === */}
                {(isAdmin || isVP) && (
                    <Card className="border-emerald-200 bg-gradient-to-br from-emerald-50 to-white" data-testid="tool-section-rejoindre">
                        <CardContent className="p-5 sm:p-7">
                            <div className="flex flex-col lg:flex-row lg:items-start gap-5">
                                <div className="flex-shrink-0">
                                    <div className="w-14 h-14 rounded-xl bg-emerald-100 flex items-center justify-center">
                                        <ClipboardCheck className="w-7 h-7 text-emerald-600" />
                                    </div>
                                </div>
                                <div className="flex-1">
                                    <div className="flex items-center gap-2">
                                        <span className="inline-flex items-center justify-center text-[11px] font-bold text-emerald-700 bg-emerald-100 rounded-full px-2 py-0.5">ÉTAPE 1</span>
                                        <h3 className="text-lg font-bold text-slate-900">Formulaire de candidature — « Rejoindre Valnexo »</h3>
                                    </div>
                                    <p className="text-sm text-slate-600 mt-2 leading-relaxed">
                                        C'est la <strong>toute première étape</strong> du parcours. Toute nouvelle personne qui souhaite
                                        nous rejoindre doit remplir ce formulaire : <strong>sa candidature ne devient officielle qu'une fois
                                        ce formulaire complété</strong>.
                                    </p>
                                    <div className="mt-3 flex items-start gap-2 rounded-lg border border-emerald-200 bg-emerald-50 p-3">
                                        <AlertTriangle className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" />
                                        <p className="text-xs text-emerald-800">
                                            Une fois la candidature reçue et <strong>validée par vous et la direction</strong>, vous pourrez
                                            alors transmettre le <strong>lien d'onboarding (étape 2)</strong> ci-dessous.
                                        </p>
                                    </div>
                                    <p className="text-xs text-slate-400 mt-2 break-all">{REJOINDRE_LINK}</p>
                                </div>
                                <div className="flex-shrink-0 flex flex-col gap-2">
                                    <Button onClick={() => openExternal(REJOINDRE_LINK)} variant="outline" className="gap-2" data-testid="open-rejoindre-link-btn">
                                        <ExternalLink className="w-4 h-4" />
                                        Ouvrir
                                    </Button>
                                    <Button
                                        onClick={() => { navigator.clipboard.writeText(REJOINDRE_LINK); toast.success('Lien copié !'); }}
                                        className="gap-2 bg-emerald-600 hover:bg-emerald-700"
                                        data-testid="copy-rejoindre-link-btn"
                                    >
                                        <Copy className="w-4 h-4" />
                                        Copier le lien
                                    </Button>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                )}

                {/* === Lien Onboarding nouveaux apporteurs (Admin + VP uniquement) === */}
                {(isAdmin || isVP) && (
                    <Card className="border-purple-200 bg-gradient-to-br from-purple-50 to-white" data-testid="tool-section-onboarding">
                        <CardContent className="p-5 sm:p-7">
                            <div className="flex flex-col lg:flex-row lg:items-start gap-5">
                                <div className="flex-shrink-0">
                                    <div className="w-14 h-14 rounded-xl bg-purple-100 flex items-center justify-center">
                                        <UserPlus className="w-7 h-7 text-purple-600" />
                                    </div>
                                </div>
                                <div className="flex-1">
                                    <div className="flex items-center gap-2">
                                        <span className="inline-flex items-center justify-center text-[11px] font-bold text-purple-700 bg-purple-100 rounded-full px-2 py-0.5">ÉTAPE 2</span>
                                        <h3 className="text-lg font-bold text-slate-900">Lien d'onboarding — nouveaux apporteurs</h3>
                                    </div>
                                    <p className="text-sm text-slate-600 mt-2">
                                        Page d'inscription à la formation pour vos futurs apporteurs.
                                    </p>
                                    <div className="mt-3 flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3">
                                        <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
                                        <p className="text-xs text-amber-800">
                                            À ne transmettre <strong>uniquement</strong> que lorsque le postulant a rempli le formulaire
                                            « Rejoindre Valnexo » et qu'il a été validé par <strong>vous et la direction</strong>.
                                        </p>
                                    </div>
                                    <p className="text-xs text-slate-400 mt-2 break-all">{ONBOARDING_LINK}</p>
                                </div>
                                <div className="flex-shrink-0 flex flex-col gap-2">
                                    <Button onClick={() => openExternal(ONBOARDING_LINK)} variant="outline" className="gap-2" data-testid="open-onboarding-link-btn">
                                        <ExternalLink className="w-4 h-4" />
                                        Ouvrir
                                    </Button>
                                    <Button
                                        onClick={() => { navigator.clipboard.writeText(ONBOARDING_LINK); toast.success('Lien copié !'); }}
                                        className="gap-2 bg-purple-600 hover:bg-purple-700"
                                        data-testid="copy-onboarding-link-btn"
                                    >
                                        <Copy className="w-4 h-4" />
                                        Copier le lien
                                    </Button>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                )}

                {/* === SECTION 3 — Plaquette === */}
                <Card data-testid="tool-section-plaquette">
                    <CardContent className="p-5 sm:p-7">
                        <div className="flex flex-col lg:flex-row lg:items-center gap-5">
                            <div className="flex-shrink-0">
                                <div className="w-14 h-14 rounded-xl bg-purple-100 flex items-center justify-center">
                                    <FileText className="w-7 h-7 text-purple-600" />
                                </div>
                            </div>
                            <div className="flex-1">
                                <h3 className="text-lg font-bold text-slate-900">Plaquette de présentation</h3>
                                <p className="text-sm text-slate-600 mt-1">
                                    Une version courte et synthétique de la home page, idéale à envoyer en suivi de premier
                                    rendez-vous.
                                </p>
                                <p className="text-xs text-slate-400 mt-1 break-all">{VALNEXO_PLAQUETTE}</p>
                            </div>
                            <div className="flex-shrink-0">
                                <Button onClick={() => openExternal(VALNEXO_PLAQUETTE)} variant="outline" className="gap-2" data-testid="open-plaquette-btn">
                                    <ExternalLink className="w-4 h-4" />
                                    Ouvrir la plaquette
                                </Button>
                            </div>
                        </div>
                    </CardContent>
                </Card>

                {/* === SECTION 4 — Cartes de visite === */}
                <Card data-testid="tool-section-cards">
                    <CardContent className="p-5 sm:p-7">
                        <div className="flex flex-col lg:flex-row lg:items-center gap-5">
                            <div className="flex-shrink-0">
                                <div className="w-14 h-14 rounded-xl bg-rose-100 flex items-center justify-center">
                                    <IdCard className="w-7 h-7 text-rose-600" />
                                </div>
                            </div>
                            <div className="flex-1">
                                <h3 className="text-lg font-bold text-slate-900">
                                    {isApporteur ? 'Ma carte de visite' : 'Cartes de visite'}
                                </h3>
                                {loadingCard ? (
                                    <p className="text-sm text-slate-500 mt-1 flex items-center gap-2">
                                        <Loader2 className="w-4 h-4 animate-spin" /> Chargement...
                                    </p>
                                ) : !businessCard ? (
                                    <p className="text-sm text-slate-500 mt-1">Information indisponible.</p>
                                ) : businessCard.kind === 'admin_directory' ? (
                                    <>
                                        <p className="text-sm text-slate-600 mt-1">
                                            {isAdmin
                                                ? 'Accédez à l\'annuaire complet des cartes de visite de l\'équipe Valnexo.'
                                                : 'Consultez votre carte ou celles de votre équipe depuis l\'annuaire.'}
                                        </p>
                                        <p className="text-xs text-slate-400 mt-1 break-all">{businessCard.url}</p>
                                    </>
                                ) : businessCard.url ? (
                                    <>
                                        <p className="text-sm text-slate-600 mt-1">
                                            Voici votre carte de visite personnelle prête à être partagée avec vos prospects.
                                        </p>
                                        <p className="text-xs text-slate-400 mt-1 break-all">{businessCard.url}</p>
                                        <p className="text-xs text-amber-700 mt-2 bg-amber-50 border border-amber-200 rounded p-2 leading-relaxed" data-testid="card-fallback-message">
                                            Si votre carte de visite n'apparaît pas, c'est qu'elle n'a pas encore été créée.
                                            Réclamez-la à votre responsable secteur s'il vous plaît, merci !
                                        </p>
                                    </>
                                ) : (
                                    <p className="text-sm text-slate-700 mt-1 bg-amber-50 border border-amber-200 rounded p-3 leading-relaxed" data-testid="card-missing-message">
                                        Votre carte de visite n'est pas encore créée. Réclamez-la à votre responsable secteur s'il vous plaît, merci !
                                    </p>
                                )}
                            </div>
                            {businessCard?.url && !loadingCard && (
                                <div className="flex-shrink-0">
                                    <Button onClick={() => openExternal(businessCard.url)} variant="outline" className="gap-2" data-testid="open-card-btn">
                                        <ExternalLink className="w-4 h-4" />
                                        {businessCard.label || 'Ouvrir'}
                                    </Button>
                                </div>
                            )}
                        </div>
                    </CardContent>
                </Card>

                {/* === Modal prospect picker === */}
                <Dialog open={showProspectPicker} onOpenChange={setShowProspectPicker}>
                    <DialogContent className="max-w-2xl">
                        <DialogHeader>
                            <DialogTitle>Sélectionner le prospect</DialogTitle>
                            <DialogDescription>
                                Choisissez le prospect auquel rattacher ce formulaire d'étude.
                            </DialogDescription>
                        </DialogHeader>
                        <div className="relative mb-3">
                            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                            <Input
                                placeholder="Tapez un nom, société, ville, email..."
                                value={prospectQuery}
                                onChange={(e) => setProspectQuery(e.target.value)}
                                onKeyDown={handlePickerKeyDown}
                                className="pl-9"
                                data-testid="prospect-picker-search"
                                autoFocus
                            />
                            {prospects.length > 0 && (
                                <div className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-slate-400">
                                    {filteredProspects.length}/{prospects.length}
                                </div>
                            )}
                        </div>
                        <div className="max-h-[60vh] overflow-y-auto divide-y divide-slate-100 border rounded-lg">
                            {prospects.length === 0 ? (
                                <div className="py-8 text-center text-sm text-slate-400 flex items-center justify-center gap-2">
                                    <Loader2 className="w-4 h-4 animate-spin" /> Chargement des prospects...
                                </div>
                            ) : filteredProspects.length === 0 ? (
                                <div className="py-8 text-center text-sm text-slate-400">Aucun prospect trouvé</div>
                            ) : (
                                filteredProspects.slice(0, 200).map((p, idx) => (
                                    <button
                                        key={p.id}
                                        type="button"
                                        onClick={() => handlePickProspect(p)}
                                        disabled={openingForm}
                                        className={`w-full text-left px-4 py-2.5 hover:bg-blue-50 flex items-center justify-between transition-colors disabled:opacity-50 ${idx === 0 && prospectQuery ? 'bg-blue-50/50' : ''}`}
                                        data-testid={`pick-prospect-${p.id}`}
                                    >
                                        <div className="min-w-0 flex-1">
                                            <div className="font-medium text-slate-900 truncate">
                                                {p.company_name || `${p.first_name || ''} ${p.last_name || ''}`.trim() || 'Sans nom'}
                                            </div>
                                            <div className="text-xs text-slate-500 mt-0.5 truncate">
                                                {[
                                                    p.company_name && `${p.first_name || ''} ${p.last_name || ''}`.trim(),
                                                    p.city,
                                                    p.email,
                                                ].filter(Boolean).join(' · ')}
                                            </div>
                                        </div>
                                        <ChevronRight className="w-4 h-4 text-slate-400 flex-shrink-0 ml-2" />
                                    </button>
                                ))
                            )}
                        </div>
                        {prospectQuery && filteredProspects.length > 0 && !openingForm && (
                            <div className="text-xs text-slate-500 mt-2 text-center">
                                Appuyez sur <kbd className="px-1.5 py-0.5 bg-slate-100 rounded border text-[10px] font-mono">Entrée</kbd> pour sélectionner le premier résultat
                            </div>
                        )}
                        {openingForm && (
                            <div className="flex items-center justify-center gap-2 text-sm text-slate-600 mt-2">
                                <Loader2 className="w-4 h-4 animate-spin" /> Génération du lien sécurisé...
                            </div>
                        )}
                    </DialogContent>
                </Dialog>
            </div>
        </DashboardLayout>
    );
}
